import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { EnhancedToolResult, ToolExecutionContext } from '../types';

import { REVIEW_CHECKS, type ReviewFinding } from './review-checks';
import { Tool } from './Tool';

const checkIds = REVIEW_CHECKS.map((check) => check.id) as [string, ...string[]];

export const reportFindingsSchema = z.object({
    findings: z
        .array(
            z.object({
                check: z
                    .enum(checkIds)
                    .describe('The id of the defect from the list you were given.'),
                scene_id: z.string().describe('The scene the defect is in, exactly as labelled.'),
                detail: z
                    .string()
                    .describe(
                        'What is wrong, concrete enough to act on without seeing the frames.',
                    ),
            }),
        )
        .describe('Every defect you found. An empty list means the scenes are clean.'),
});
export type ReportFindingsArgs = z.infer<typeof reportFindingsSchema>;

/**
 * The reviewer's only way to answer. Given to the child vision conversation as its sole tool and
 * used as its stop tool, so the review comes back as validated structure rather than prose —
 * prose is what let the agent read a defect as a suggestion.
 */
export class ReportFindingsTool extends Tool<typeof reportFindingsSchema> {
    public captured: ReviewFinding[] | undefined;

    protected override readonly schema = reportFindingsSchema;
    protected override readonly toolName = TOOL_NAMES.reportFindings;
    protected override readonly toolDescription =
        'Report every defect you found, or an empty list if there are none. This is how you answer.';

    public override getToolDisplayName(): string {
        return 'Reporting findings';
    }

    public get statusIcon(): string {
        return 'rule';
    }

    public async execute(
        args: ReportFindingsArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        this.captured = args.findings as ReviewFinding[];
        return { result: `Recorded ${args.findings.length} finding(s).`, isSuccess: true };
    }
}
